import React, {
  useCallback,
  useRef,
  useMemo,
  forwardRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { nanoid } from "@reduxjs/toolkit";
import _ from "lodash";
import { Logger } from "@base-provider";
import {
  getGridData,
  getGridFilterValues,
} from "../../services/dataGridService";
import SFTreeFilter from "./treeFilter";
import SFVirtualTreeFilter from "./virtualTreeFilter";
import {
  setRowCount,
  setRequestHash,
} from "../../redux/reducers/gridLayoutReducer";
import { CELL_RENDERER, HEADER_RENDERED } from "./dataGridConfig";
import SFDataGridCore from "./dataGridCore";
import { GRID_FILTERS } from "./dataGridConfig";
import { getUnixTimestamp, getUtcDateTime } from "../../services/dateService";
import SFLoadingErrorCard from "../loadingErrorCard";
import { sha256 } from "../../helpers/hash";
import { getCustomComponents, resolveGroupTreeFilterModel } from "./helpers";
import SFDataGridSettingsDrawer from "./settingsDrawer";
import SFDataGridSettingsButton from "./settingsDrawer/settingsButton";

const SFDataGrid = forwardRef((props, ref) => {
  const {
    allowMultipleDetailRows = true,
    persistState = true,
    hasRowSelection = false,
    hasColSelection = false,
    hideGrid = false,
    enableStatusBar = true,
    enableResponseCaching = false,
    suppressRowClickSelection = true,
    cardVariant = "card",
    onRowCountChange = () => {},
    masterDetail = false,
    gridName,
    columnDefs = [],
    detailColumnDefs = null,
    datasourceUrl,
    viewMasterDetailsUrl,
    maxBlocksInCache = null,
    // Tree data props for server-side hierarchical data
    treeData = false,
    treeDataChildrenUrl = null,
    isServerSideGroup = null,
    getServerSideGroupKey = null,
    // Tree data selection config for calculating parent/child selection counts
    // { childCountField: string, parentLabel?: string, childLabel?: string }
    treeDataSelectionConfig = null,
    ...otherProps
  } = props;

  const savedLayout = useSelector((state) => {
    return state.gridLayout.find(
      (gridLayout) => gridLayout.gridName === gridName
    );
  });
  const internalRef = useRef();
  const gridRef = ref ?? internalRef;

  // Ref to always hold the current totalChildrenCount from Redux
  // This prevents stale closure issues when filters change and the callback
  // is invoked before re-render with new savedLayout value
  const totalChildrenCountRef = useRef(savedLayout?.totalChildrenCount ?? 0);
  
  // Keep the ref synchronized with Redux state
  if (savedLayout?.totalChildrenCount !== undefined) {
    totalChildrenCountRef.current = savedLayout.totalChildrenCount;
  }

  const dispatch = useDispatch();

  const [rowData, setRowData] = useState(null);
  const [loadingRows, setLoadingRows] = useState(false);
  const [errorOccured, setErrorOccured] = useState(false);
  const [columnsDrawerOpen, setColumnsDrawerOpen] = useState(false);

  //this field is hidden and used as a helper to disable the rows checkboxes if the select all checkbox is checked
  const CHECKBOX_DISABLED_FIELD = "checkboxDisabled";

  const buildColumns = useCallback(
    (gridColumnDefs, type = "master") => {
      Logger.of("App.Core.DataGrid.buildColumns").info({
        gridName,
        type,
        gridColumnDefs,
      });
      gridColumnDefs = gridColumnDefs.map((colDef) => {
        // Extract custom properties that should go to context
        const {
          selection,
          tooltip,
          tooltipWidth,
          tooltipPosition,
          filterTooltip,
          useCustomHeader,
          tooltipVariant,
          onTooltipClick,
          showChips,
          align,
          variant,
          mustGrow,
          treeFilter,
          trueIcon,
          falseIcon,
          filterModel,
          // Cell renderer custom props
          alignItems,
          avatar,
          textVariant,
          textComponent,
          onCheck,
          styleOptions,
          // Filter/other custom props
          supressFilterModelFromValues,
          justGrow,
          ignoreFilterOnGetValues,
          showBasicFilterDisabled,
          afterFilterChanged,
          nullValueFilterLabel,
          groupByColumn,
          allowGrow,
          groupFilter,
          treeStructure,
          filterField,
          treeFilterValuesUrl,
          boldText,
          isGroupFilter,
          rowHeight,
          persistBasicFiltersApply,
          ...validColDef
        } = colDef;

        return {
          ...validColDef,
          // Store custom props in context for internal usage
          context: {
            ...colDef.context,
            selection,
            tooltip,
            tooltipWidth,
            tooltipPosition,
            filterTooltip,
            useCustomHeader,
            tooltipVariant,
            onTooltipClick,
            showChips,
            align,
            variant,
            mustGrow,
            treeFilter,
            trueIcon,
            falseIcon,
            filterModel,
            // Cell renderer custom props
            alignItems,
            avatar,
            textVariant,
            textComponent,
            onCheck,
            styleOptions,
            // Filter/other custom props
            supressFilterModelFromValues,
            justGrow,
            ignoreFilterOnGetValues,
            showBasicFilterDisabled,
            afterFilterChanged,
            nullValueFilterLabel,
            groupByColumn,
            allowGrow,
            groupFilter,
            treeStructure,
            filterField,
            treeFilterValuesUrl,
            boldText,
            isGroupFilter,
            rowHeight,
            persistBasicFiltersApply,
            // Store original filter type for basicFiltering (before converting custom types to true)
            originalFilterType: colDef.filter,
          },
          ...((variant == "gridTrendRender" ||
            colDef.cellRenderer == CELL_RENDERER.TREND ||
            colDef.type == "trend") && {
            maxWidth: 100,
            headerClass: "ag-center-aligned-header",
            cellClass: "ag-center-aligned-cell",
          }),
          ...((colDef.filter == "agNumberColumnFilter" ||
            colDef.type == "number") && {
            type: "numericColumn",
          }),
          ...(mustGrow && {
            flex: 1,
            suppressSizeToFit: true,
            resizable: false,
          }),
          // Note: Custom selection columns have been removed. Row selection is now handled
          // entirely by AG Grid's built-in rowSelection configuration (rowSelection.checkboxes),
          // so no dedicated selection column definition is required here.
          //DEFAULT CELL RENDERER - SFCellTextRenderer
          cellRenderer: colDef.cellRenderer ?? CELL_RENDERER.TEXT,
          // Map custom filter types to valid AG Grid filters
          // Custom types (category, hierarchy, dropdown, boolean, radio) are handled by basicFiltering
          filter: treeFilter
            ? treeFilter === "virtualTreeFilter"
              ? SFVirtualTreeFilter
              : SFTreeFilter
            : (["category", "hierarchy", "dropdown", "boolean", "radio"].includes(colDef.filter)
              ? true // Use default AG Grid filter, actual filtering handled by basicFiltering
              : colDef.filter),
          headerComponent: (!_.isNil(tooltip) || (useCustomHeader && colDef.wrapHeaderText))
            ? HEADER_RENDERED.DEFAULT
            : "agColumnHeader",
          // If there is a saved sort model don't use default sorting
          sort:
            savedLayout?.columnModel?.filter((col) => col.sort).length > 0
              ? null
              : colDef.sort,
        };
      });
      if (type === "master") {
        gridColumnDefs.push({
          field: "searchText",
          hide: true,
          suppressFiltersToolPanel: true,
          suppressColumnsToolPanel: true,
          context: { showChips: false },
          filter: "agTextColumnFilter",
          filterParams: {
            debounceMs: 0,
          },
        });
      }
      //if any of the columnDefs contains property selection and showDisabledCheckboxes is enabled (which means that we have row selection enabled)
      //we add a hidden field checkboxDisabled to the columnDefs array
      //the purpose of this field is to update its value every time the select all checkbox changes (if exists)
      //in order to disable the checkboxes of the rows if the select all checkbox is checked
      //via the checkboxSelection callback (see above)
      if (
        gridColumnDefs.find(
          (x) =>
            (x.context?.selection || x.selection) &&
            (x.showDisabledCheckboxes || x.context?.showDisabledCheckboxes)
        ) !== undefined
      ) {
        gridColumnDefs.push({
          field: CHECKBOX_DISABLED_FIELD,
          hide: true,
          suppressFiltersToolPanel: true,
          suppressColumnsToolPanel: true,
          context: { showChips: false },
          filter: false,
        });
      }
      //check if columnDefs contains key advancedQuery, if so push a hidden field advancedQuery
      // this will be used to apply the advanced query
      if (gridColumnDefs.some((x) => x.filterParams?.advancedQuery)) {
        gridColumnDefs.push({
          field: "advancedQuery",
          hide: true,
          suppressFiltersToolPanel: true,
          suppressColumnsToolPanel: true,
          context: { showChips: false },
          filter: "agTextColumnFilter",
        });
      }
      // Check if the 'hasColSelection' prop is set to true, if so, add a pinned column to the end of the columnDefs array
      // whose headerComponent is a button that opens the column settings drawer
      if (hasColSelection) {
        gridColumnDefs.push({
          field: "colSelection",
          headerName: "",
          headerComponent: () => (
            <SFDataGridSettingsButton
              onClick={() => setColumnsDrawerOpen(true)}
            />
          ),
          headerClass: "ag-center-aligned-header no-padding",
          cellClass: "ag-center-aligned-cell no-padding",
          pinned: "right",
          suppressColumnsToolPanel: true,
          suppressMovable: true,
          resizable: false,
          sortable: false,
          // Prevent the column from being unpinned
          lockPinned: true,
          width: 55,
          maxWidth: 55,
          minWidth: 55,
        });
      }
      return gridColumnDefs;
    },
    [hasColSelection]
  );

  //CREATING COLUMN DEFINITIONS BASED ON PROPS
  //Ag-grid suggests to use the useMemo hook to memoize the columnDefs array
  //we update the columnDefs array only when the columnDefs prop changes (need also to memoize out of this component) or the buildColumns function changes (which is wrapped in useCallback)
  const colDefs = useMemo(
    () => buildColumns(columnDefs),
    [columnDefs, buildColumns]
  );
  const detailedColDefs = useMemo(
    () => detailColumnDefs && buildColumns(detailColumnDefs, "detail"),
    [detailColumnDefs, buildColumns]
  );

  //Request filter values from api, should be declared before defaultColDefs
  //used by ag grid internally and filter value is required to be returned from this
  //function in order for the grid to trigger the request with the new filter
  const getValues = useCallback(
    (params) => {
      //here we check if the column belongs to the group filter
      //and if so we dont trigger the request to get filter values , but we
      //return values from the saved layout
      if (!params.colDef.groupFilter) {
        let filterModel = _.cloneDeep(params.api.getFilterModel());
        resolveGroupTreeFilterModel(filterModel);
        resolveDateFilterModel(filterModel);
        if (filterModel["searchText"]) {
          delete filterModel["searchText"];
        }
        // The end result of the following code is that it removes properties from the 'filterModel' object
        // if the corresponding column definition object has its 'ignoreFilterOnGetValues' property set to true.
        colDefs
          .filter(
            (columnDef) =>
              columnDef.ignoreFilterOnGetValues &&
              columnDef.field in filterModel
          )
          .forEach((columnDef) => delete filterModel[columnDef.field]);
        getGridFilterValues(
          otherProps.filterValuesUrl,
          JSON.stringify({
            colName: params.colDef.field,
            //remove grouped columns
            filterModel: filterModel,
          })
        )
          .then((resp) => resp.data.values)
          .then((filterValues) => {
            params.success(filterValues);
          });
      }
    },
    [colDefs]
  );

  const getDetailServerSideDataSource = async (id, filterModel) => {
    let response = await getGridData(
      viewMasterDetailsUrl.replace(/{id}/, id),
      filterModel
    );
    return response.data;
  };

  //callback function used to update rowData state
  const updateRowData = (rowData, merge = false) => {
    if (merge) {
      setRowData((prev) => [...prev, ...rowData]);
    } else {
      setRowData(rowData);
    }
  };

  //callback function used to update loadingRows state
  const updateLoadingRows = (isLoading) => {
    setLoadingRows(isLoading);
  };

  const handleError = () => {
    setErrorOccured(true);
  };

  const getServerSideDataSource = () => {
    // For tree data, use a specialized datasource that handles parent/child hierarchy
    if (treeData && treeDataChildrenUrl) {
      // Get the field name from autoGroupColumnDef for sorting mapping
      const autoGroupField = otherProps.autoGroupColumnDef?.field;
      return createTreeDataServerSideDatasource(
        datasourceUrl,
        treeDataChildrenUrl,
        otherProps.configureRequestPayload,
        updateRowData,
        updateLoadingRows,
        handleError,
        (response) => {
          if (persistState) {
            dispatch(
              setRowCount({
                gridName,
                rowCount: response.rowCount ?? 0,
                totalChildrenCount: response.totalChildrenCount,
              })
            );
          }
        },
        onRowCountChange,
        autoGroupField
      );
    }
    
    // Standard server-side datasource for non-tree data
    let server = createRequest(
      datasourceUrl,
      null,
      otherProps.configureRequestPayload,
      updateRowData,
      updateLoadingRows
    );
    return createServerSideDatasource(
      server,
      (response) => {
        if (persistState) {
          if (otherProps.rowCountUrl) {
            let filterModel = _.cloneDeep(
              gridRef.current?.api.getFilterModel()
            );
            if (filterModel) {
              resolveGroupTreeFilterModel(filterModel);
              resolveGroupFilterModel(filterModel, colDefs);
            }
            getGridData(otherProps.rowCountUrl, {
              filterModel,
            }).then((res) =>
              dispatch(
                setRowCount({
                  gridName,
                  rowCount: res.data.rowCount,
                })
              )
            );
          } else {
            dispatch(
              setRowCount({
                gridName,
                rowCount: response.rowCount ?? 0,
              })
            );
          }
        }
        resizeColumns();
      },
      colDefs,
      dispatch,
      updateRowData,
      handleError,
      onRowCountChange
    );
  };

  const onModelUpdated = useCallback((params) => {
    resizeColumns();
    otherProps.onModelUpdated && otherProps.onModelUpdated(params);
  }, []);

  const postFilterChange = (filterModel, params) => {
    let filteredArr = params.columns.filter((x) => x.colDef.afterFilterChanged ?? x.colDef.context?.afterFilterChanged);
    if (typeof filteredArr !== "undefined") {
      filteredArr.forEach((col) => {
        let values = filterModel[col.colDef.field]?.values ?? "";
        const afterFilterChanged = col.colDef.afterFilterChanged ?? col.colDef.context?.afterFilterChanged;
        afterFilterChanged(values);
      });
    }
  };

  const beforeDownload = (filterModel) => {
    resolveGroupTreeFilterModel(filterModel);
  };

  const resizeColumns = () => {
    if (gridRef.current?.api) {
      if (otherProps.fitScreen) {
        gridRef.current?.api.sizeColumnsToFit();
      } else {
        const allColumnIds = [];
        gridRef.current.api.getColumns().forEach((column) => {
          allColumnIds.push(column.getId());
        });
        gridRef.current.api.autoSizeColumns(allColumnIds, false);
      }
    }
  };

  const getRowId = useCallback((params) => {
    if (otherProps.getRowId) {
      return otherProps.getRowId(params);
    }
    // use nanoid in case row hasn't id (eg. row group)
    return params.data?.id ?? nanoid();
  }, []);

  const getDetailRowId = useCallback((params) => {
    if (otherProps.detailCellRendererParams?.detailGridOptions?.getRowId) {
      return otherProps.detailCellRendererParams.detailGridOptions.getRowId(
        params
      );
    }
    // use nanoid in case row hasn't id
    return params.data?.id ?? nanoid();
  }, []);

  //custom component should be memoized to avoid re-rendering
  const customComponents = useMemo(
    () => getCustomComponents(otherProps.customComponents),
    []
  );

  const detailCellRendererParams = useMemo(() => {
    return (masterParams) => {
      return {
        ...otherProps.detailCellRendererParams,
        detailGridOptions: {
          ...otherProps.detailCellRendererParams?.detailGridOptions,
          getRowId: getDetailRowId,
          columnDefs: detailedColDefs,
          components: customComponents,
          defaultColDef: {
            flex: 1,
            //hides menu, filter and context buttons shown next to column headers
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
          },
          context: {
            gridName,
            entityType: otherProps.entityType,
            params: masterParams,
          },
        },
        getDetailRowData: (params) => {
          if (otherProps.getDetailRowData) {
            return params.successCallback(otherProps.getDetailRowData(params));
          }
          // apply filter model to detail grid
          let filterModel = params.context?.detailFilterModel ?? {};
          let data;
          if (Object.keys(filterModel).length > 0) {
            data = { filterModel };
          }
          getDetailServerSideDataSource(params.data.id, data).then((res) =>
            params.successCallback(res.rowData),
          );
        },
      };
    };
  }, [detailedColDefs]);

  const onSelectionChanged = useCallback(
    (params, selectionChanged = false) => {
      const rawState = params.api.getServerSideSelectionState();
      let selection = { ...rawState };

      //every time the selection changes we update the checkboxDisabled field property based on the select all value
      //this is used to trigger the checkboxSelection callback and disable all the rows checkboxes if the select all checkbox is checked
      //NOTE: every time a value of a field is updated, the checkboxselection callback is triggered
      if (
        colDefs.find(
          (x) => x.field === CHECKBOX_DISABLED_FIELD && selectionChanged,
        )
      ) {
        params.api.forEachNode((node) => {
          node.setDataValue(CHECKBOX_DISABLED_FIELD, selection.selectAll);
        });
      }
      selection.filter = _.cloneDeep(params.api.getFilterModel());
      resolveGroupTreeFilterModel(selection.filter);

      // Always store raw AG Grid state for restoration
      selection.rawSelectionState = rawState;
      
      // Calculate tree selection child count if tree data with selection config
      // Simple approach: track count incrementally
      // - On page reload: use stored count from Redux
      // - On user action or filter change: calculate based on current state
      if (treeData && treeDataSelectionConfig) {
        const childCountField = treeDataSelectionConfig.childCountField;
        const existingChildCount = savedLayout?.selection?.treeChildCount ?? 0;
        const isSelectAll = rawState?.selectAllChildren;
        // Use ref to get current totalChildrenCount to avoid stale closure issues
        // when filters change and the callback is invoked with outdated savedLayout
        const totalMicroskillCount = totalChildrenCountRef.current;
        
        // For non-user-initiated events (page refresh/restoration), preserve existing count from Redux
        // This is important because during restoration, the AG Grid nodes may not have their
        // selection state properly reflected yet, so recalculating would give wrong results.
        // The stored treeChildCount was correctly calculated when the user made the selection.
        if (!selectionChanged) {
          selection.treeChildCount = existingChildCount;
          return selection;
        }
        
        let childCount = 0;
        
        if (isSelectAll) {
          // Select all mode: start with total, subtract deselected children
          childCount = totalMicroskillCount;
          params.api.forEachNode((node) => {
            if (node.level === 0) {
              const selectionState = node.isSelected();
              if (selectionState === false) {
                // Parent is fully deselected - subtract all its children
                childCount -= node.data?.[childCountField] || 0;
              } else if (selectionState === undefined) {
                // Indeterminate parent - has some children selected/deselected
                // Find the toggled node info to determine what toggledNodes represents
                const parentToggled = rawState?.toggledNodes?.find(
                  (t) => t.nodeId === node.data?.id
                );
                if (parentToggled) {
                  const totalChildren = node.data?.[childCountField] || 0;
                  const toggledCount = parentToggled.toggledNodes?.length || 0;
                  if (parentToggled.selectAllChildren) {
                    // Parent was selected (selectAllChildren=true), some children deselected
                    // toggledNodes contains deselected children
                    childCount -= toggledCount;
                  } else {
                    // Parent was deselected (selectAllChildren=false), some children selected
                    // toggledNodes contains selected children
                    // We need to subtract (totalChildren - selectedCount) since we started with all selected
                    childCount -= (totalChildren - toggledCount);
                  }
                }
              }
              // selectionState === true means fully selected, no subtraction needed
            }
          });
        } else {
          // Manual selection mode: sum up children of selected parents
          params.api.forEachNode((node) => {
            if (node.level === 0) {
              const selectionState = node.isSelected();
              if (selectionState === true) {
                // Fully selected parent - add all its children
                childCount += node.data?.[childCountField] || 0;
              } else if (selectionState === undefined) {
                // Indeterminate parent - has some children selected/deselected
                const parentToggled = rawState?.toggledNodes?.find(
                  (t) => t.nodeId === node.data?.id
                );
                if (parentToggled) {
                  const totalChildren = node.data?.[childCountField] || 0;
                  const toggledCount = parentToggled.toggledNodes?.length || 0;
                  if (parentToggled.selectAllChildren) {
                    // Parent was fully selected, some children deselected
                    // toggledNodes contains deselected children
                    childCount += totalChildren - toggledCount;
                  } else {
                    // Parent was not selected, some children manually selected
                    // toggledNodes contains selected children
                    childCount += toggledCount;
                  }
                }
              }
            }
          });
        }
        
        selection.treeChildCount = childCount;
      }
      
      return selection;
    },
    [colDefs, treeData, treeDataSelectionConfig, savedLayout]
  );

  if (errorOccured) {
    return <SFLoadingErrorCard />;
  }

  return (
    <React.Fragment>
      <SFDataGridCore
        {...props}
        //enable for debug mode
        // debug
        ref={gridRef}
        loadingRows={loadingRows}
        enableStatusBar={enableStatusBar}
        persistState={persistState}
        getValues={getValues}
        //enables master detail
        masterDetail={masterDetail}
        {...(masterDetail &&
          viewMasterDetailsUrl && {
          detailCellRendererParams,
        })}
        viewDetailsUrl={otherProps.viewDetailsUrl}
        allowMultipleDetailRows={allowMultipleDetailRows}
        viewDetails={
          otherProps.permissions?.viewDetails ?? otherProps.permissions?.view
        }
        edit={otherProps.permissions?.edit}
        delete={otherProps.permissions?.delete}
        onModelUpdated={onModelUpdated}
        detailProps={otherProps.detailProps}
        fitScreen={otherProps.fitScreen}
        rowModelType={"serverSide"}
        //decides whether to load all rows or load each block on scroll
        serverSideInfiniteScroll={true}
        //number of rows to load per request
        cacheBlockSize={50}
        //number of rows to save in cache  5 * 20 = 100 rows, when requests exceed that number memory is cleared (default to unlimited)
        maxBlocksInCache={maxBlocksInCache}
        //custom renderers. could have an impact on grid lag. Need to test in production as indicated by ag grid documentation
        customRenderers={customComponents}
        columnDefs={colDefs}
        onSelectionChanged={onSelectionChanged}
        suppressRowClickSelection={suppressRowClickSelection}
        hasRowSelection={hasRowSelection}
        getRowId={getRowId}
        serverSideDataSource={getServerSideDataSource}
        // Tree data props
        treeData={treeData}
        isServerSideGroup={isServerSideGroup}
        getServerSideGroupKey={getServerSideGroupKey}
        postFilterChange={postFilterChange}
        gridName={gridName}
        rowData={rowData}
        columnGroupOptions={otherProps.columnGroupOptions}
        beforeDownloadFilter={beforeDownload}
        filterValuesUrl={otherProps.filterValuesUrl}
        rowCountUrl={otherProps.rowCountUrl}
        disableSaveSelection={otherProps.disableSaveSelection}
        enableResponseCaching={enableResponseCaching}
        cacheMinAge={otherProps.cacheMinAge}
        cardVariant={cardVariant}
        cardColumns={otherProps.cardColumns}
        hasColSelection={hasColSelection}
        hideGrid={hideGrid}
      />
      {hasColSelection && (
        <SFDataGridSettingsDrawer
          open={columnsDrawerOpen}
          onClose={() => setColumnsDrawerOpen(false)}
          ref={ref}
        />
      )}
    </React.Fragment>
  );
});

function createServerSideDatasource(
  server,
  onDataRetrieved,
  colDefs,
  dispatch,
  updateRowData,
  handleError,
  onRowCountChange
) {
  return {
    getRows: async function (params) {
      if (params.request.filterModel) {
        resolveGroupTreeFilterModel(params.request.filterModel);
        resolveGroupFilterModel(params.request.filterModel, colDefs);
        resolveDateFilterModel(params.request.filterModel);
      }

      let response;
      // perform request if caching not enabled
      if (!params.context.enableResponseCaching) {
        try {
          response = await server.getData(params.request);
        } catch (error) {
          handleError();
        }
      } else {
        // calculate hash for request object
        const hashKey = await sha256(JSON.stringify(params.request));
        const responseCacheExpirationMs =
          1000 * 60 * params.context.cacheRefreshInterval;
        const savedLayout = params.context.savedLayout;

        // retrieve from redux if exists and not expired
        if (
          savedLayout &&
          savedLayout.hashes[hashKey] &&
          savedLayout.hashes[hashKey].timestamp >
            getUnixTimestamp() - responseCacheExpirationMs &&
          savedLayout.hashes[hashKey].timestamp >
            getUnixTimestamp(params.context.cacheMinAge)
        ) {
          response = JSON.parse(savedLayout.hashes[hashKey].response);
          updateRowData(response.rowData);
        } else {
          // perform request if not in redux or expired
          response = await server.getData(params.request);
          let hashValue = {
            gridName: params.context.gridName,
            hash: hashKey,
            response: JSON.stringify(response),
          };
          // save hash to redux
          dispatch(setRequestHash(hashValue));
        }
      }
      if (response && response.success) {
        onDataRetrieved(response);
        onRowCountChange(response.rowCount);
        params.success({
          rowData: response.rowData,
        });
      } else {
        params.fail();
      }
    },
  };
}

function createRequest(
  url,
  params,
  configureRequestPayload,
  updateRowData,
  updateLoadingRows
) {
  return {
    getData: async (_request) => {
      updateLoadingRows(true);
      let request = configureRequestPayload
        ? configureRequestPayload(_request)
        : _request;
      let response = await getGridData(url, JSON.stringify(request), params);
      let result = response.data;
      if (request.startRow === 0) {
        updateRowData(result.rowData);
      } else {
        updateRowData(result.rowData, true);
      }
      updateLoadingRows(false);
      return {
        success: true,
        rowCount: result.rowCount,
        rowData: result.rowData,
      };
    },
  };
}

/**
 * Creates a server-side datasource for tree data with lazy-loaded children.
 * 
 * @param {string} parentUrl - URL for fetching root level (parent) data
 * @param {string} childrenUrl - URL template for fetching children data (use {id} as placeholder for parent ID)
 * @param {function} configureRequestPayload - Optional function to customize request payload
 * @param {function} updateRowData - Callback to update row data state
 * @param {function} updateLoadingRows - Callback to update loading state
 * @param {function} handleError - Callback to handle errors
 * @param {function} onDataRetrieved - Callback when data is retrieved (for row count updates)
 * @param {function} onRowCountChange - Callback when row count changes
 * @param {string} autoGroupColumnField - The field name for the auto group column (default: "name")
 */
function createTreeDataServerSideDatasource(
  parentUrl,
  childrenUrl,
  configureRequestPayload,
  updateRowData,
  updateLoadingRows,
  handleError,
  onDataRetrieved,
  onRowCountChange,
  autoGroupColumnField
) {
  return {
    getRows: async function (params) {
      const { groupKeys, startRow, endRow, sortModel, filterModel } = params.request;
      
      try {
        // Only show loading for root level requests
        if (groupKeys.length === 0) {
          updateLoadingRows(true);
        }
        
        // ColId can be configured for autoGroupColumn in v35.0.0
        // Map ag-Grid-AutoColumn to the actual field name in sortModel
        const mappedSortModel = (sortModel || []).map(sort => ({
          ...sort,
          colId: sort.colId === "ag-Grid-AutoColumn" ? autoGroupColumnField : sort.colId,
        }));
        
        // Build base request payload
        let requestPayload = {
          startRow,
          endRow,
          sortModel: mappedSortModel,
          filterModel: filterModel || {},
          pivotMode: false,
          rowGroupCols: [],
          valueCols: [],
          pivotCols: [],
          groupKeys: [],
        };
        
        // Apply custom payload configuration if provided
        if (configureRequestPayload) {
          requestPayload = configureRequestPayload(requestPayload);
        }
        
        let url;
        if (groupKeys.length === 0) {
          // Root level: fetch parent data
          url = parentUrl;
        } else {
          // Child level: fetch children for the parent
          const parentId = groupKeys[0];
          url = childrenUrl.replace(/{id}/, parentId);
        }
        
        const response = await getGridData(url, JSON.stringify(requestPayload));
        const result = response.data;
        
        let rowData = result.rowData;
        
        // Only update state and row count for root level
        if (groupKeys.length === 0) {
          // Notify about data retrieval and row count
          // totalChildrenCount is expected from API for tree data (e.g., total microskills)
          onDataRetrieved({ 
            rowCount: result.rowCount,
            totalChildrenCount: result.totalChildrenCount,
          });
          onRowCountChange(result.rowCount);
          updateLoadingRows(false);
        }
        
        params.success({
          rowData: rowData,
          rowCount: result.rowCount,
        });
      } catch (error) {
        if (groupKeys.length === 0) {
          updateLoadingRows(false);
        }
        handleError();
        params.fail();
      }
    },
  };
}

function resolveGroupFilterModel(filterModel, colDefs) {
  if (colDefs.find((x) => x.isGroupFilter)) {
    Object.keys(filterModel).forEach((k) => {
      if (colDefs.find((x) => x.field === k)?.isGroupFilter) {
        filterModel[k].isGroupFilter = true;
      }
    });
  }
}

// Function to re-format dates in getValues and getRows functions
// to a format that the API can accept
export function resolveDateFilterModel(filterModel) {
  Object.keys(filterModel).forEach((k) => {
    if (filterModel[k].filterType === GRID_FILTERS.filterType.date) {
      filterModel[k].dateFrom =
        filterModel[k].dateFrom && getUtcDateTime(filterModel[k].dateFrom);
      filterModel[k].dateTo =
        filterModel[k].dateTo && getUtcDateTime(filterModel[k].dateTo);
    }
  });
}

SFDataGrid.propTypes = {
  /* Any custom permissions to be applied on grid actions  */
  permissions: PropTypes.shape({
    export: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    add: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    delete: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    edit: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    view: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    viewDetails: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
  }),
  /* Name of file data to download  */
  exportFileName: PropTypes.string,
  /* The API url from which grid requests row data  */
  datasourceUrl: PropTypes.string.isRequired,
  /* The API url from which grid requests values to show in filters  */
  filterValuesUrl: PropTypes.string,
  /* The API url from which grid requests the count of total rows based on the selected filter  */
  rowCountUrl: PropTypes.string,
  /* The API url from which grid requests values to show in filters  */
  viewDetailsUrl: PropTypes.string,
  /* >The API ulr from which grid request data for master-detail*/
  viewMasterDetailsUrl: PropTypes.string,
  /* Decides if grid should have a detail component  */
  masterDetail: PropTypes.bool,
  /* The name of the grid that is used to save values in the  redux slice  */
  gridName: PropTypes.string.isRequired,
  /* An array of columns definitions as objects  */
  columnDefs: PropTypes.arrayOf(
    PropTypes.shape({
      headerName: PropTypes.string,
      field: PropTypes.string,
    }).isRequired
  ).isRequired,
  /* Decides what component will be used when rendering details. Should be passed as a function */
  detailCellRenderer: PropTypes.func,
  gridState: PropTypes.number,
  detailHeight: PropTypes.number,
  /* Decides whether multiple detail components can be open at the same time */
  allowMultipleDetailRows: PropTypes.bool,
  /* Decides if grid sorting and filtering state are saved */
  persistState: PropTypes.bool,
  /* Any data that needs to be passed in the detail component */
  detailProps: PropTypes.any,
  /* Allow multiple row selection */
  rowSelection: PropTypes.oneOf(["single", "multiple"]),
  /* Enable row selection */
  hasRowSelection: PropTypes.bool,
  /* Enable columns selection */
  hasColSelection: PropTypes.bool,
  /* Any grid config regarding filter ,sorting and selection */
  gridConfig: PropTypes.shape({
    sortModel: PropTypes.array,
    rowGroupKey: PropTypes.string,
    filterModel: PropTypes.object,
    selection: PropTypes.shape({
      allSelected: PropTypes.bool,
      toggledNodes: PropTypes.arrayOf(PropTypes.string),
      selectedCount: PropTypes.number,
      unselectableRowCount: PropTypes.number,
    }),
    rowCount: PropTypes.number,
  }),
  columnGroupOptions: PropTypes.array,
  /*
  Set this to TRUE to stop the grid from rendering so you can use the filters of the grid without rendering the grid data
  If this is set to true the grid will not query the API to get actual grid data however the filters will still query the API
  This function requires column definitions etc too for this to work
  */
  hideGrid: PropTypes.bool,
  card: PropTypes.func,
  /* The variant of the card to use for the SFResponsiveGrid */
  cardVariant: PropTypes.string,
  /* The breakpoint values if we want to use SFGrid */
  cardColumns: PropTypes.object,
  hasBorder: PropTypes.bool,
  enableStatusBar: PropTypes.bool,
  /*Flag that disables redux state for selection */
  disableSaveSelection: PropTypes.bool,
  /*Flag that enables caching responses from the API in redux */
  enableResponseCaching: PropTypes.bool,
  /*Number of blocks to save in cache, when requests exceed that number memory is cleared (default to unlimited) */
  maxBlocksInCache: PropTypes.number,
  /*If timestamp for response cache object is older than this, it should refresh */
  cacheMinAge: PropTypes.string,
  /*Interval in minutes to force refresh response cache object */
  cacheRefreshInterval: PropTypes.number,
  /* If true (default), rows cannot be selected when clicked (checkbox selection still works) */
  suppressRowClickSelection: PropTypes.bool,
  /**
   * Callback fired when the row count of a server-side grid changes (i.e. when the grid performs a new request to the server).
   * This function is useful when you want to expose the row count to the parent component (for example, to display it in a
   * separate component) when `persistState` is not set to true.
   */
  onRowCountChange: PropTypes.func,
  /**
   * Enable tree data mode for server-side hierarchical data.
   * When enabled, the grid will use isServerSideGroup and getServerSideGroupKey callbacks to manage hierarchy.
   * Selection will automatically use groupSelects: 'descendants' for tri-state checkbox behavior.
   */
  treeData: PropTypes.bool,
  /**
   * URL template for fetching children data in tree data mode.
   * Use {id} as placeholder for parent ID, e.g., "api/parent/{id}/children/grid"
   */
  treeDataChildrenUrl: PropTypes.string,
  /**
   * Callback to determine if a row is a group (has children) in tree data mode.
   * Returns true if the row can be expanded to show children.
   * Example: (dataItem) => dataItem.childCount > 0
   */
  isServerSideGroup: PropTypes.func,
  /**
   * Callback to get the unique key for a group row in tree data mode.
   * This key is passed to the children URL to fetch child rows.
   * Example: (dataItem) => dataItem.id
   */
  getServerSideGroupKey: PropTypes.func,
  /**
   * Configuration for tree data selection aggregation.
   * childCountField: required field name on the data item that stores the number of selected children.
   * parentLabel / childLabel: optional labels used for displaying selection summaries.
   */
  treeDataSelectionConfig: PropTypes.shape({
    childCountField: PropTypes.string.isRequired,
    parentLabel: PropTypes.string,
    childLabel: PropTypes.string,
  }),
};

export default SFDataGrid;
